if (!window.PlpV2ProductCard) {
  window.PlpV2ProductCard = {
    toastTimeout: null,
    wishlistObservers: new WeakMap(),
    initialized: false,

    init() {
      if (this.initialized) return;
      this.initialized = true;

      this.handleClick = this.handleClick.bind(this);
      this.handleVariantClick = this.handleVariantClick.bind(this);
      this.handleFacetFiltersUpdated = this.handleFacetFiltersUpdated.bind(this);

      document.addEventListener('click', this.handleClick, true);
      document.addEventListener('click', this.handleVariantClick, true);
      document.addEventListener('on:facet-filters:updated', this.handleFacetFiltersUpdated);

      this.bindWishlistButtons();
      this.observeWishlistHosts();

      // Wishlist King and the image slider init after deferred app scripts.
      window.setTimeout(() => {
        this.bindWishlistButtons();
        this.observeWishlistHosts();
        window.dispatchEvent(new Event('on:debounced-resize'));
      }, 500);
    },

    getLengthClickBehavior() {
      return window.theme?.settings?.cardLengthClickBehavior || 'update_card';
    },

    getVariants(card) {
      try {
        return JSON.parse(card.dataset.variants || '[]');
      } catch (error) {
        return [];
      }
    },

    buildVariantUrl(baseUrl, variantId) {
      if (!baseUrl) return '';
      const separator = baseUrl.includes('?') ? '&' : '?';
      return `${baseUrl}${separator}variant=${variantId}`;
    },

    updateProductLinks(card, variantUrl) {
      card.querySelectorAll('a.js-prod-link').forEach((link) => {
        link.href = variantUrl;
      });
    },

    updatePrice(card, variant) {
      const priceCurrentStrong = card.querySelector('.price__current > strong');
      const priceCompareAt = card.querySelector('.price__default > s.price__was');
      const priceFromEl = card.querySelector('.price__default .price__from');

      if (priceCurrentStrong) {
        priceCurrentStrong.textContent = variant.price || '';
      }

      if (priceCompareAt) {
        if (variant.compareAtPriceAmount > variant.priceAmount) {
          priceCompareAt.textContent = variant.compareAtPrice || '';
          priceCompareAt.classList.remove('hide');
        } else {
          priceCompareAt.classList.add('hide');
        }
      }

      priceFromEl?.classList.add('hide');
    },

    updateQuickAddButton(card, variant) {
      const quickAddBtn = card.querySelector('.js-quick-add');
      if (!quickAddBtn) return;

      quickAddBtn.dataset.selectedVariantId = String(variant.id);
      card.dataset.selectedVariantId = String(variant.id);

      const textEl = quickAddBtn.querySelector('.quick-add-btn-text');
      if (textEl) {
        textEl.textContent = quickAddBtn.dataset.addToCartText
          || window.theme?.strings?.plpV2Card?.addToCart
          || 'Add to cart';
      }

      if (typeof card.selectedVariantId !== 'undefined') {
        card.selectedVariantId = String(variant.id);
      }
    },

    markActiveLengthOption(card, variantId) {
      card.querySelectorAll('.product-block-options__item[data-variant-id]').forEach((item) => {
        item.classList.toggle('is-active', String(item.dataset.variantId) === String(variantId));
      });
    },

    clearLengthFacetIfNeeded(card, variantId) {
      const params = new URLSearchParams(window.location.search);
      const hadLengthFacet =
        params.has('filter.p.m.gebruik.werklengte')
        || params.has('filter.p.m.gebruik.lengte_stelen');

      if (!hadLengthFacet) return;

      params.delete('filter.p.m.gebruik.werklengte');
      params.delete('filter.p.m.gebruik.lengte_stelen');

      const facetFiltersEl = document.querySelector('facet-filters');
      if (!facetFiltersEl || typeof facetFiltersEl.applyFilters !== 'function') return;

      const productUrlRaw = card.dataset.url
        || card.querySelector('.js-quick-add')?.dataset.productUrl
        || '';

      let productPath = '';
      try {
        productPath = new URL(productUrlRaw, window.location.origin).pathname;
      } catch (error) {
        productPath = productUrlRaw;
      }

      try {
        sessionStorage.setItem(
          'hc-plp-swatch-restore',
          JSON.stringify({
            productPath,
            variantId,
            kind: 'length'
          })
        );
      } catch (error) {
        // sessionStorage may be unavailable in private mode.
      }

      facetFiltersEl.applyFilters(params.toString(), null);
    },

    selectVariant(card, variantId, options = {}) {
      const { navigate = false } = options;
      const variants = this.getVariants(card);
      const variant = variants.find((entry) => String(entry.id) === String(variantId));

      if (!variant) return false;

      const baseUrl = card.dataset.url;
      const variantUrl = this.buildVariantUrl(baseUrl, variantId);

      if (navigate) {
        if (variantUrl) window.location.assign(variantUrl);
        return true;
      }

      this.markActiveLengthOption(card, variantId);
      this.updateProductLinks(card, variantUrl);
      this.updatePrice(card, variant);
      this.updateQuickAddButton(card, variant);
      this.updateFulfillment(card, variantId);
      this.clearLengthFacetIfNeeded(card, variantId);

      return true;
    },

    handleFacetFiltersUpdated() {
      this.bindWishlistButtons();
      this.observeWishlistHosts();
      window.setTimeout(() => {
        this.bindWishlistButtons();
        this.observeWishlistHosts();
        window.dispatchEvent(new Event('on:debounced-resize'));
      }, 500);
    },

    bindWishlistButtons() {
      document.querySelectorAll('product-card.card--plp-v2, .card--plp-v2').forEach((card) => {
        this.bindWishlistButtonsInCard(card);
      });
    },

    bindWishlistButtonsInCard(card) {
      card.querySelectorAll('wishlist-button-collection, wishlist-link').forEach((host) => {
        this.observeWishlistHost(card, host);
      });

      this.bindWishlistButtonListeners(card);
    },

    observeWishlistHost(card, host) {
      const rebind = () => {
        this.observeWishlistHostShadow(card, host);
        this.bindWishlistButtonListeners(card);
      };

      if (host.dataset.plpV2HostObserved !== 'true') {
        host.dataset.plpV2HostObserved = 'true';

        const attrObserver = new MutationObserver(rebind);
        attrObserver.observe(host, { attributes: true, attributeFilter: ['wk-loaded'] });

        const hostObserver = new MutationObserver(rebind);
        hostObserver.observe(host, { childList: true, subtree: true });
      }

      rebind();
    },

    observeWishlistHostShadow(card, host) {
      if (!host.shadowRoot || host.dataset.plpV2ShadowObserved === 'true') return;
      host.dataset.plpV2ShadowObserved = 'true';

      const shadowObserver = new MutationObserver(() => this.bindWishlistButtonListeners(card));
      shadowObserver.observe(host.shadowRoot, { childList: true, subtree: true });
    },

    bindWishlistButtonListeners(card) {
      this.findWishlistButtons(card).forEach((button) => {
        if (button.dataset.plpV2WishlistBound === 'true') return;
        button.dataset.plpV2WishlistBound = 'true';

        let wasSelected = button.classList.contains('wk-selected');

        const observer = new MutationObserver(() => {
          const isSelected = button.classList.contains('wk-selected');
          if (isSelected && !wasSelected) {
            this.showWishlistToast(card);
          }
          wasSelected = isSelected;
        });

        observer.observe(button, { attributes: true, attributeFilter: ['class'] });
      });
    },

    observeWishlistHosts() {
      document.querySelectorAll('product-card.card--plp-v2, .card--plp-v2').forEach((card) => {
        if (this.wishlistObservers.has(card)) return;

        const observer = new MutationObserver(() => {
          this.bindWishlistButtonsInCard(card);
        });

        observer.observe(card, { childList: true, subtree: true });
        this.wishlistObservers.set(card, observer);
      });
    },

    findWishlistButtons(card) {
      const buttons = [];

      card.querySelectorAll('wishlist-button-collection, wishlist-link').forEach((host) => {
        const roots = [host];
        if (host.shadowRoot) roots.push(host.shadowRoot);

        roots.forEach((root) => {
          root.querySelectorAll('.wk-button, button.wk-button').forEach((button) => {
            buttons.push(button);
          });
        });
      });

      card.querySelectorAll('.wk-button, wishlist-link').forEach((button) => {
        if (!buttons.includes(button)) buttons.push(button);
      });

      return buttons;
    },

    isWishlistSelectedInCard(card) {
      return this.findWishlistButtons(card).some((button) => button.classList.contains('wk-selected'));
    },

    getWishlistClickContext(event) {
      const path = typeof event.composedPath === 'function' ? event.composedPath() : [event.target];

      let card = null;
      let wishlistTarget = null;

      path.forEach((node) => {
        if (!(node instanceof Element)) return;

        if (!card && node.matches('product-card.card--plp-v2, .card--plp-v2')) {
          card = node;
        }

        if (
          !wishlistTarget &&
          (node.matches('.wk-button') ||
            node.matches('wishlist-link') ||
            node.matches('wishlist-button-collection') ||
            node.matches('wk-button'))
        ) {
          wishlistTarget = node;
        }
      });

      return { card, wishlistTarget };
    },

    handleClick(event) {
      const { card, wishlistTarget } = this.getWishlistClickContext(event);
      if (!card || !wishlistTarget) return;

      const wasSelected = this.isWishlistSelectedInCard(card);

      window.setTimeout(() => {
        const isSelected = this.isWishlistSelectedInCard(card);
        if (isSelected && !wasSelected) {
          this.showWishlistToast(card);
        }
      }, 300);
    },

    handleVariantClick(event) {
      const option = event.target.closest('.card--plp-v2 .product-block-options__item[data-variant-id]');
      if (!option || option.classList.contains('product-block-options__item--unavailable')) return;

      const card = option.closest('product-card.card--plp-v2');
      if (!card) return;

      event.preventDefault();
      event.stopPropagation();

      const variantId = option.dataset.variantId;
      if (!variantId) return;

      const navigate = this.getLengthClickBehavior() === 'product_page';
      this.selectVariant(card, variantId, { navigate });
    },

    showWishlistToast(card) {
      const toast = card.querySelector('.plp-v2-wishlist-toast');
      if (!toast) return;

      toast.removeAttribute('hidden');
      toast.hidden = false;
      toast.classList.add('is-visible');

      window.clearTimeout(this.toastTimeout);
      this.toastTimeout = window.setTimeout(() => {
        toast.classList.remove('is-visible');
        window.setTimeout(() => {
          toast.hidden = true;
          toast.setAttribute('hidden', '');
        }, 200);
      }, 2800);
    },

    updateFulfillment(card, variantId) {
      const variants = this.getVariants(card);
      const variant = variants.find((entry) => String(entry.id) === String(variantId));
      if (!variant) return;

      const fulfillmentEl = card.querySelector('.plp-v2-card-fulfillment');
      const stockEl = card.querySelector('.js-plp-v2-stock-status');
      const deliveryEl = card.querySelector('.js-plp-v2-delivery-estimate');
      const iconEl = card.querySelector('.plp-v2-card-fulfillment__icon');
      if (!stockEl) return;

      const strings = window.theme?.strings?.plpV2Card || {};
      const isInventoryManaged = variant.inventoryManaged === true;
      const inventoryQuantity = Number(variant.inventoryQuantity);
      let inventoryState = variant.available ? 'in_stock' : 'sold_out';

      if (variant.available && isInventoryManaged && inventoryQuantity <= 0) {
        inventoryState = variant.inventoryPolicy === 'continue' ? 'backordered' : 'sold_out';
      }

      fulfillmentEl?.setAttribute('data-inventory-state', inventoryState);

      if (inventoryState === 'in_stock') {
        stockEl.textContent = strings.inStock || 'In stock';
        deliveryEl?.classList.remove('hidden');
        fulfillmentEl?.classList.remove('plp-v2-card-fulfillment--sold-out');
        iconEl?.classList.remove('hidden');
      } else {
        stockEl.textContent = inventoryState === 'backordered'
          ? (strings.backordered || 'Backordered')
          : (strings.soldOut || 'Sold out');
        deliveryEl?.classList.add('hidden');
        fulfillmentEl?.classList.add('plp-v2-card-fulfillment--sold-out');
        iconEl?.classList.add('hidden');
      }
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => window.PlpV2ProductCard.init());
  } else {
    window.PlpV2ProductCard.init();
  }
}
